import React, { useRef, useState } from "react";
import BottomSheet from "../BottomSheet";
import { Camera, FileText } from "lucide-react";
import CameraUpload from "../../common/CameraUpload";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import { useParams } from "react-router-dom";

interface RecordsUploadBottomSheetProps {
  showBottomSheet: boolean;
  setShowBottomSheet: (show: boolean) => void;
  isUploadAgain?: boolean;
  onFileUpload: (fileData: any) => void;
  //onCameraStateChange?: (isActive: boolean) => void;
}

const RecordsUploadBottomSheet = ({
  showBottomSheet,
  setShowBottomSheet,
  isUploadAgain = false,
  onFileUpload,
}: //onCameraStateChange,
RecordsUploadBottomSheetProps) => {
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [isPdfFile, setIsPdfUpload] = useState(false);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const handleCameraClick = () => {
    // Event: CAMERA_CLICKED
    analytics.trackEvent(
      `camera_${EVENT_NAMES.RECORD_UPLOAD_CLICKED}_from_bottom_sheet`,
      {
        screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
        support_program_key: supportProgramKey,
        card_title: "Upload Record Bottom Sheet",
      }
    );
    setShowCamera(true);
    // onCameraStateChange?.(true);
  };

  const handleGalleryClick = () => {
    // Event: GALLERY_CLICKED
    analytics.trackEvent(
      `gallery_${EVENT_NAMES.RECORD_UPLOAD_CLICKED}_from_bottom_sheet`,
      {
        screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
        support_program_key: supportProgramKey,
        card_title: "Upload Record Bottom Sheet",
      }
    );
    setShowBottomSheet(false);
    (galleryInputRef.current as HTMLInputElement | null)?.click();
  };

  const handleFileClick = () => {
    // Event: FILE_CLICKED
    analytics.trackEvent(
      `file_${EVENT_NAMES.RECORD_UPLOAD_CLICKED}_from_bottom_sheet`,
      {
        screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
        support_program_key: supportProgramKey,
        card_title: "Upload Record Bottom Sheet",
      }
    );
    setShowBottomSheet(false);
    (fileInputRef.current as HTMLInputElement | null)?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).map((file) => {
      // Ensure mime type is set
      if (!file.type) {
        // Determine mime type based on file extension
        const extension = file.name.split(".").pop()?.toLowerCase();
        const mimeTypes = {
          pdf: "application/pdf",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
        };
        return new File([file], file.name, {
          type:
            mimeTypes[extension as keyof typeof mimeTypes] ||
            "application/octet-stream",
        });
      }
      return file;
    });
    // onFileUpload({ fileData: files });
    setIsPdfUpload(files[0].type === "application/pdf");
    onFileUpload({
      fileData: files,
      isUploadAgain,
    });
  };

  const handleCameraDone = (images: any) => {
    // Handle multiple captured images
    const filePromises = images.map((imageData: any, index: number) =>
      fetch(imageData)
        .then((res) => res.blob())
        .then((blob) => {
          return new File([blob], `camera_${index + 1}.jpg`, {
            type: "image/jpeg",
          });
        })
    );

    // Wait for all files to be processed and send them together
    Promise.all(filePromises)
      .then((files) => {
        onFileUpload({
          fileData: files,
          isUploadAgain,
        });
      })
      .catch((error) => {
        console.error("Error processing images:", error);
      });
  };

  return (
    <React.Fragment>
      <BottomSheet
        show={showBottomSheet && !showCamera}
        handleClose={() => setShowBottomSheet(false)}
        showCloseButton
      >
        <div className="relative w-full">
          <div className="p-4">
            <h1 className="text-lg font-semibold text-black">Upload Record</h1>
          </div>
          <div className="flex flex-col">
            {[
              {
                label: "Use Camera",
                icon: <Camera className="w-4 h-4" />,
                onClick: handleCameraClick,
              },
              {
                label: "Upload from Gallery",
                icon: <FileText className="w-4 h-4" />,
                onClick: handleGalleryClick,
              },
              {
                label: "Upload from Files",
                icon: <FileText className="w-4 h-4" />,
                onClick: handleFileClick,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 border-t border-gray-300 px-4 py-3 text-gray-700 cursor-pointer"
                onClick={item.onClick}
              >
                <div className="p-2 rounded-full bg-[var(--icon-bg-primary)] text-[var(--app-primary)]">
                  {item.icon}
                </div>
                <span className="text-base">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </BottomSheet>

      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept="application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        type="file"
        ref={galleryInputRef}
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {showCamera && (
        <CameraUpload
          onClose={() => {
            setShowCamera(false);
            setShowBottomSheet(false);
            //onCameraStateChange?.(false);
          }}
          onCapture={() => {}}
          onDone={handleCameraDone}
        />
      )}
    </React.Fragment>
  );
};

export default RecordsUploadBottomSheet;
